import { cloneDeep, find, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  IDashboardChartData,
  IDashboardHomeCardMetrics,
  IDashboardLiveData,
  LiveItemsKey,
  queryHomeDashboardLiveData,
} from '~/src/dash/api/dashboards/home';
import { DashboardCardsV2 } from '~/src/dash/components/cards/DashboardCardsV2';
import { IDashboardChartMetric, mergeCompareData } from '~/src/dash/components/charts/common';
import { CompareChart } from '~/src/dash/components/charts/CompareChart';
import {
  FILTER_PILLS,
  FilterPillKey,
  getDashboardFilterParams,
  getInitialDashboardFilterParams,
} from '~/src/dash/components/filters/common';
import { ClockIcon } from '~/src/dash/components/icons/misc-icons';
import { IconTextToggle } from '~/src/dash/components/toggles/IconTextToggle';
import { MenuToggle } from '~/src/dash/components/toggles/MenuToggle';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
} from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { defaultTimeRanges, findTimeRangeByKey, TimeRange } from '~/src/dash/utils/dateTimeRanges';
import { useInterval } from '~/src/dash/utils/hooks';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { getTimezone } from '~/src/dash/utils/timezone';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  addDefaultSelected,
  clearedActiveFilters,
  DashboardLiveCardGroup,
  extractDataForMultiLineChart,
  extractViableLiveMetrics,
  getLiveCardGroup,
  IActiveFilters,
  refreshChannelsFilter,
  updateActiveChartMetrics,
  updateDefaultSelectedForFilters,
  updateDisplayedMetricKeys,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DashboardFilterMenuToggle } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardFilterMenuToggle';
import { DashboardFilters } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardFilters';
import { DashboardSelector } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardSelector';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import { IDashboardHomeCardsConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { DashboardSubChart } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardLive/DashboardSubChart';
import { DashboardSubChartMetricsSelector } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardLive/DashboardSubChartMetricsSelector';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const allDeviceTypesQueryString =
  'DEVICETYPE_UNKNOWN,PERSONAL_COMPUTER,CONNECTED_TV,HIGHEND_PHONE,TABLET,CONNECTED_DEVICE,SET_TOP_BOX';

const getPublicaAnalyticsItemsKey = (
  activeFilters: IActiveFilters,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterParams: Record<string, any>,
): LiveItemsKey[] => {
  const { demand, supply, device, publisher, cluster, domain, region, predictiveSmoothing } =
    activeFilters;
  const keys = [] as LiveItemsKey[];
  if (region) {
    keys.push('publica_region');
    return keys;
  } else if (device) {
    if (publisher) {
      keys.push('device_type');
      return keys;
    } else {
      keys.push('publica_device_type');
      return keys;
    }
  }

  if (!publisher && !demand && !supply && domain && !predictiveSmoothing) {
    keys.push('publica_domain');
    return keys;
  }
  if (publisher && !demand && !supply && domain && !predictiveSmoothing) {
    keys.push('publisher_publica_domain');
    return keys;
  }
  if (!publisher && !demand && !supply && cluster && !predictiveSmoothing) {
    keys.push('publica_cluster');
    return keys;
  }
  if (!publisher && demand && !supply && !cluster && !predictiveSmoothing) {
    keys.push('publica_biddername');
    return keys;
  }
  if (publisher && !demand && supply && !cluster && !predictiveSmoothing) {
    keys.push('site');
    return keys;
  }
  if (
    !publisher &&
    !demand &&
    supply === 'Auction Requesters' &&
    !cluster &&
    !predictiveSmoothing
  ) {
    keys.push('publica_auction_requester');
    return keys;
  }
  if (publisher && demand && supply === 'Channels' && cluster && !predictiveSmoothing) {
    keys.push('publica_cluster_site_id_demand_source');
    return keys;
  }
  if (publisher && demand && !supply && !cluster && !predictiveSmoothing) {
    keys.push('biddername');
    return keys;
  }
  if (publisher && demand && supply && !cluster && !predictiveSmoothing) {
    keys.push('site_biddername');
    return keys;
  }
  if (publisher && demand && !supply && cluster && !predictiveSmoothing) {
    keys.push('biddername_publica_cluster');
    return keys;
  }
  if (publisher && !demand && !supply && cluster && !predictiveSmoothing) {
    keys.push('publisher_publica_cluster');
    return keys;
  }
  if (!publisher && demand && !supply && cluster && !predictiveSmoothing) {
    keys.push('publica_cluster_demand_source');
    return keys;
  }
  if (publisher && !demand && !device && supply === 'Channels' && cluster && !predictiveSmoothing) {
    keys.push('publica_cluster_site_id');
    return keys;
  }
  if (publisher && demand && supply === 'Channels' && cluster && predictiveSmoothing) {
    if (filterParams.predictive_smoothing === 'PredictiveSmoothingDisabled') {
      keys.push('publica_cluster_site_id_demand_source');
    }
    keys.push('ps_pub_id_publica_cluster_site_id_demand_source');
    return keys;
  }
  if (publisher && !demand && !device && supply === 'Channels' && cluster && predictiveSmoothing) {
    if (filterParams.predictive_smoothing === 'PredictiveSmoothingDisabled') {
      keys.push('publica_cluster_site_id');
    }
    keys.push('ps_pub_id_publica_cluster_site_id');
    return keys;
  }
  if (publisher && !demand && supply && !cluster && predictiveSmoothing) {
    if (filterParams.predictive_smoothing === 'PredictiveSmoothingDisabled') {
      keys.push('site');
    }
    keys.push('ps_pub_id_site_id');
    return keys;
  }
  keys.push('publisher_id');
  return keys;
};

const isOnlyDevice = (activeFilters: IActiveFilters) => {
  const { demand, supply, publisher, cluster, region, requester, domain } = activeFilters;
  return !demand && !supply && !publisher && !cluster && !region && !requester && !domain;
};

// IMPORTANT: IF YOU ARE UPDATING PUBLICA ANALYTICS, CHECK IF LIVE MUST BE UPDATED TOO
// IMPORTANT: PLEASE READ THE README BEFORE EDITING -> https://publica-project.atlassian.net/wiki/x/DQDXpQ
export const DashboardPublicaAnalytics = () => {
  const { session, user, currencyType, isSuperUser } = useBaseStore();
  const { Dashboards } = useBaseConfig();
  const cfg = Dashboards.PublicaAnalytics;

  const initialCardGroup = getLiveCardGroup(cfg.defaultState.activeFilters);
  const initialDisplayedMetricKeys = updateDisplayedMetricKeys(
    cfg.cards.groupings[initialCardGroup],
    cfg.defaultState.displayedMetricKeys,
  );
  const initialActiveChartMetrics = updateActiveChartMetrics(
    initialDisplayedMetricKeys,
    cfg.defaultState.activeChartMetrics,
    cfg.cards.metrics,
  );
  const defaultTimezone =
    cfg.defaultState.timezone || getTimezone(user.user.Timezone, session.publisher.Timezone);

  const isMounted = useRef(false);
  const isInitialized = useRef(false);
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [timeRange, setTimeRange] = useState(findTimeRangeByKey(cfg.defaultState.timeRange));
  const [displayedMetricKeys, setDisplayedMetricKeys] = useState(initialDisplayedMetricKeys);
  const [activeChartMetrics, setActiveChartMetrics] = useState(initialActiveChartMetrics);
  const [filtersLoadState, setFiltersLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState({} as Record<string, IMenuList>);
  const [selectedFilters, setSelectedFilters] = useState({} as Record<string, IMenuList>);
  const [isDemandRefreshing, setIsDemandRefreshing] = useState(false);
  const [activeFilters, setActiveFilters] = useState(cfg.defaultState.activeFilters);
  const [cardGroup, setCardGroup] = useState(initialCardGroup);
  const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(cfg.defaultState.isPeriodMenuOpen);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(cfg.defaultState.isFilterMenuOpen);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [data, setData] = useState({} as IDashboardLiveData);
  const [isDataRefreshActive, setIsDataRefreshActive] = useState(false);
  const [lastRefreshedTime, setLastRefreshedTime] = useState('');
  const [orderedBidErrorKeys, setOrderedBidErrorKeys] = useState([] as [string, number][]);

  useEffect(() => {
    getDashboardLiveData();
    getFilters();
    track('Dash Publica Analytics - View', { isSuperUser }, user.user);
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      setIsDataRefreshActive(false);
      getDashboardLiveData();
    }
  }, [timeRange]);

  useEffect(() => {
    if (isMounted.current && filtersLoadState === LoadingStateHasData && isInitialized.current) {
      setIsDataRefreshActive(false);
      getDashboardLiveData();
    }
  }, [selectedFilters]);

  useInterval(
    () => {
      getDashboardLiveData(true);
    },
    isDataRefreshActive ? 60 * 1000 : null,
  );

  useEffect(() => {
    if (isMounted.current) {
      updateDefaultSelectedForFilters(selectedFilters, cfg.filters, cfg.defaultState);
      if (!isInitialized.current) isInitialized.current = true;
    }
    isMounted.current = true;
  }, [selectedFilters]);

  const handleToggleTimeRange = (rangeKey: TimeRange) => {
    setTimeRange(findTimeRangeByKey(rangeKey));
  };

  const getDashboardLiveData = (isRefreshCall?: boolean) => {
    if (!isRefreshCall) setLoadState(LoadingStateLoading);

    const updatedCardGroup = getLiveCardGroup(activeFilters);
    const filterParams =
      isInitialized.current && filtersLoadState !== LoadingStateLoading
        ? getDashboardFilterParams(selectedFilters, cfg.filters, activeFilters)
        : getInitialDashboardFilterParams(cfg.filters, activeFilters);

    const isPublicaDeviceType = isOnlyDevice(activeFilters);

    const query: Record<string, any> = {
      is_publica_analytics: true,
      publisher_id: session.publisher.ID,
      timezone,
      currency_type: currencyType,
      live_density_range: timeRange.value,
      live_items_key: isPublicaDeviceType
        ? 'publica_device_type'
        : getPublicaAnalyticsItemsKey(activeFilters, filterParams).join(','),
      ...filterParams,
    };
    if (isPublicaDeviceType && !query.publica_device_types)
      query.publica_device_types = allDeviceTypesQueryString;

    queryHomeDashboardLiveData(session.publisher.ID, query)
      .then((result) => {
        const isDataEmpty = isEmpty(result.LiveData);
        const bidErrorCounts = result.BidErrorMetrics || {};
        const bidErrorKeys = Object.entries(bidErrorCounts).sort(
          ([, countA], [, countB]) => countB - countA,
        );

        unstable_batchedUpdates(() => {
          setData(result);
          setLoadState(LoadingStateHasData);
          setOrderedBidErrorKeys(bidErrorKeys);
          setIsDataRefreshActive(true);
          setLastRefreshedTime(moment().format('HH:mm:ss'));
          if (cardGroup !== updatedCardGroup) updateMetrics(updatedCardGroup);
          if (isDataEmpty) {
            setSnackbarError(false);
            setSnackbarMessage('No Live Data Found.');
          }
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          if (!isDataRefreshActive) {
            setData({
              LiveData: [] as IDashboardChartData[],
              CardMetrics: {} as IDashboardHomeCardMetrics,
            } as IDashboardLiveData);
          }
          setLoadState(LoadingStateNoData);
          if (cardGroup !== updatedCardGroup) updateMetrics(updatedCardGroup);
          setSnackbarError(true);
          setSnackbarMessage(
            !isDataRefreshActive
              ? 'Error retrieving data. Please wait and try again.'
              : `Error refreshing data. Data was last successfully updated at ${lastRefreshedTime}.`,
          );
        });
      });
  };

  const updateMetrics = (updatedCardGroup: DashboardLiveCardGroup) => {
    const updatedDisplayedMetricKeys = updateDisplayedMetricKeys(
      cfg.cards.groupings[updatedCardGroup],
      displayedMetricKeys,
    );
    const updatedActiveChartMetrics = updateActiveChartMetrics(
      updatedDisplayedMetricKeys,
      activeChartMetrics,
      cfg.cards.metrics,
    );
    unstable_batchedUpdates(() => {
      setDisplayedMetricKeys(updatedDisplayedMetricKeys);
      setCardGroup(updatedCardGroup);
      setActiveChartMetrics(updatedActiveChartMetrics);
    });
    cfg.defaultState.displayedMetricKeys = updatedDisplayedMetricKeys;
    cfg.defaultState.activeChartMetrics = updatedActiveChartMetrics;
  };

  // load all filters
  const getFilters = () => {
    setFiltersLoadState(LoadingStateLoading);
    let updatedFilters = {} as Record<FilterPillKey, IMenuList>;
    const updatedSelectedFilters = {} as Record<FilterPillKey, IMenuList>;
    const apiCalls = [] as Array<Promise<Record<string, IMenuList>>>;

    const filterKeys = cfg.orderedFilterKeys.filter(
      (key) => key !== 'Routes' && key !== 'Destinations',
    ) as FilterPillKey[];

    filterKeys.forEach((key) => {
      const { list, getList } = FILTER_PILLS[key];
      // if the filter requires an api call, load it
      if (getList) {
        apiCalls.push(getList(session, user));
      } else if (list) {
        updatedFilters[key] = FILTER_PILLS[key].list || ({} as IMenuList);
      }
    });
    Promise.all(apiCalls)
      .then((apiFilters) => {
        for (const filter of apiFilters) {
          updatedFilters = { ...updatedFilters, ...filter };
        }
        // if there are selected defaults, make them selected true here before continuing
        addDefaultSelected(filterKeys, updatedSelectedFilters, updatedFilters, cfg.filters);
      })
      .then(() => {
        const timezones = genTimezoneList(timezone);
        updatedFilters.Timezones = timezones;

        unstable_batchedUpdates(() => {
          setFilters(updatedFilters);
          setSelectedFilters(updatedSelectedFilters);
          setFiltersLoadState(LoadingStateHasData);
        });
      })
      .catch((err) => {
        console.error(err);
        setFiltersLoadState(LoadingStateNoData);
      });
  };

  const handleAcceptFilter = async (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    const appliedFilter = cfg.filters[key];
    const { category } = appliedFilter;
    const clonedActiveFilters = cloneDeep(activeFilters);
    if (!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
    } else if (!!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
      if (category && clonedActiveFilters[category] === key) {
        clonedActiveFilters[category] = '';
      }
    } else {
      appliedFilter.defaultSelected = Object.values(selectedOptions).map((item) => item.ID);
      if (category) clonedActiveFilters[category] = key;
    }

    // custom block for refreshing channels if the publisher changed
    const updatedFilters = {} as Record<string, IMenuList>;
    if (key === 'Publica Publishers') {
      if (appliedFilter.defaultSelected.length === 1) {
        const siteList = await refreshChannelsFilter(appliedFilter.defaultSelected[0], user);
        updatedFilters.Channels = siteList;
      }
    }

    // update states
    unstable_batchedUpdates(() => {
      setFilters({ ...filters, [key]: allOptions, ...updatedFilters });
      setSelectedFilters((selectedFilters) => ({
        ...selectedFilters,
        [key]: selectedOptions,
      }));
      setActiveFilters({ ...clonedActiveFilters });
      setIsDemandRefreshing(false);
    });
    cfg.defaultState.activeFilters = clonedActiveFilters;
    await new Promise(() => null);
  };

  const handleClearAllFilters = () => {
    const clonedFilters = cloneDeep(filters);
    const selectedFilterKeys = Object.keys(selectedFilters);
    selectedFilterKeys.forEach((filterKey) => {
      if (filterKey === 'Timezones') return;
      const selectedItemKeys = Object.keys(selectedFilters[filterKey]);
      selectedItemKeys.forEach((key) => {
        clonedFilters[filterKey][key].selected = false;
      });
      cfg.filters[filterKey].defaultSelected = [];
    });
    unstable_batchedUpdates(() => {
      setFilters(clonedFilters);
      setSelectedFilters({} as Record<string, IMenuList>);
      setActiveFilters(clearedActiveFilters);
      setIsDemandRefreshing(false);
    });
    cfg.defaultState.activeFilters = clearedActiveFilters;
  };

  const handleChangeTimezone = (
    _key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    if (isEmpty(selectedOptions)) return;

    const updatedTimezone = Object.values(selectedOptions)[0].ID;
    if (updatedTimezone === timezone) return;

    const clonedFilters = cloneDeep(filters);
    clonedFilters.Timezones = allOptions;

    unstable_batchedUpdates(() => {
      setTimezone(updatedTimezone);
      setFilters({ ...filters, Timezones: allOptions });
      setSelectedFilters({ ...selectedFilters, Timezones: selectedOptions });
    });

    cfg.defaultState.timezone = updatedTimezone;
  };

  const handleOpenFilter = () => {
    setIsDataRefreshActive(false);
  };

  const handleCloseFilter = (hasChangeOccurred: boolean) => {
    if (!hasChangeOccurred) setIsDataRefreshActive(true);
  };

  const handleOnChangeCardMetric = (
    metricKey: string,
    index: number,
    isDisplayedOnChart: boolean = false,
  ) => {
    let updatedActiveChartMetrics: IDashboardChartMetric[];
    const clonedDisplayedMetricKeys = [...displayedMetricKeys];
    // if active, replace active metric
    if (isDisplayedOnChart) {
      const clonedActiveChartMetrics = [...activeChartMetrics];
      const prevMetric = clonedDisplayedMetricKeys[index];
      updatedActiveChartMetrics = clonedActiveChartMetrics.filter(
        (metric) => metric.key !== prevMetric,
      );
      const key = metricKey as keyof IDashboardHomeCardMetrics;
      const selectedMetric = cfg.cards.metrics[key];
      updatedActiveChartMetrics.push({
        key: metricKey,
        label: selectedMetric.label,
        valueType: selectedMetric.valueType,
        chartColorIndex: index,
        chartType: selectedMetric.chartType,
        yScale: selectedMetric.yScale || 'base',
      });
    }
    clonedDisplayedMetricKeys[index] = metricKey as keyof IDashboardHomeCardMetrics;
    unstable_batchedUpdates(() => {
      setDisplayedMetricKeys(clonedDisplayedMetricKeys);
      if (isDisplayedOnChart) {
        setActiveChartMetrics(updatedActiveChartMetrics);
      }
    });
    cfg.defaultState.displayedMetricKeys = clonedDisplayedMetricKeys;
  };

  const handleOnClickCard = (metricKey: string, cardIndex: number) => {
    const key = metricKey as keyof IDashboardHomeCardMetrics;
    const clonedActiveChartMetrics = [...activeChartMetrics];
    const metric = find(clonedActiveChartMetrics, { key });
    if (!metric) {
      const selectedMetric = cfg.cards.metrics[key];
      clonedActiveChartMetrics.push({
        key: metricKey,
        label: selectedMetric.label,
        valueType: selectedMetric.valueType,
        chartColorIndex: cardIndex,
        chartType: selectedMetric.chartType,
        yScale: selectedMetric.yScale || 'base',
      });
      setActiveChartMetrics(clonedActiveChartMetrics);
      cfg.defaultState.activeChartMetrics = clonedActiveChartMetrics;
    } else {
      const filteredActiveChartMetrics = clonedActiveChartMetrics.filter(
        (metric) => metric.key !== key,
      );
      setActiveChartMetrics(filteredActiveChartMetrics);
      cfg.defaultState.activeChartMetrics = filteredActiveChartMetrics;
    }
  };

  const handlePeriodMenuToggle = () => {
    cfg.defaultState.isPeriodMenuOpen = !isPeriodMenuOpen;
    setIsPeriodMenuOpen(!isPeriodMenuOpen);
  };

  const handleFilterMenuToggle = () => {
    cfg.defaultState.isFilterMenuOpen = !isFilterMenuOpen;
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  return (
    <Panels max vertical relative minWidthMain>
      <Panel largeHorizontalPadding noVerticalPadding>
        <Cols wrap gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col style={{ minWidth: '248px' }}>
            <DashboardSelector />
          </Col>
          <Col>
            <Cols gap={spacing.extraLarge} style={{ alignItems: 'center' }}>
              <Col>
                <DashboardFilterMenuToggle
                  activeFilters={activeFilters}
                  handleOnClick={handleFilterMenuToggle}
                  isOpen={isFilterMenuOpen}
                />
              </Col>
              <Col>
                <MenuToggle
                  isActive={isPeriodMenuOpen}
                  onClick={handlePeriodMenuToggle}
                  value={timeRange.label}
                  SvgIcon={ClockIcon}
                  style={{ height: '30px' }}
                />
              </Col>
              <Col>
                <IconTextToggle
                  label='Auto-Refresh'
                  handleOnClick={() => setIsDataRefreshActive(!isDataRefreshActive)}
                  isActive={isDataRefreshActive}
                  iconSlug={isDataRefreshActive ? 'refresh' : 'pause'}
                  defaultColor={publicaColors.gray}
                  defaultBackgroundColor={publicaColors.lightGray}
                  hoverColor={publicaColors.darkGray}
                  buttonStyle={{ padding: '6px 14px 6px 12px' }}
                  tooltip={
                    <>
                      <Text bold mini color={publicaColors.white}>{`Click to ${
                        isDataRefreshActive ? 'pause' : 'start'
                      } auto-refresh.`}</Text>
                      <Text bold mini color={publicaColors.white}>
                        Last Updated: {lastRefreshedTime}
                      </Text>
                    </>
                  }
                />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Panel>
      <Panel
        largeHorizontalPadding
        smallVerticalPadding
        style={{ display: isPeriodMenuOpen || isFilterMenuOpen ? 'block' : 'none' }}
      >
        <Rows
          style={{
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: '4px',
            backgroundColor: publicaColors.white,
          }}
        >
          <Row
            horizontalPadding
            extraSmallVerticalPadding
            style={{ display: isPeriodMenuOpen ? 'block' : 'none' }}
          >
            <Cols
              extraSmallVerticalPadding
              gap={spacing.extraLarge}
              style={{ alignItems: 'center' }}
            >
              <Col>
                <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                  <Col displayFlex>
                    <ClockIcon color={publicaColors.veryDarkGray} style={{ height: '14px' }} />
                  </Col>
                  <Col>
                    <Text small bold>
                      Period
                    </Text>
                  </Col>
                </Cols>
              </Col>
              <Col>
                <DateTimeRangeToggles
                  ranges={defaultTimeRanges}
                  activeKey={timeRange.key}
                  onClick={handleToggleTimeRange}
                />
              </Col>
            </Cols>
          </Row>
          <Row
            horizontalPadding
            extraSmallVerticalPadding
            style={{ display: isFilterMenuOpen ? 'block' : 'none' }}
          >
            <DashboardFilters
              activeFilters={activeFilters}
              filters={filters}
              filtersCfg={cfg.filters}
              handleAcceptFilter={handleAcceptFilter}
              handleChangeTimezone={handleChangeTimezone}
              handleClearAllFilters={handleClearAllFilters}
              isDemandRefreshing={isDemandRefreshing}
              loadState={filtersLoadState}
              orderedFilterKeys={cfg.orderedFilterKeys as FilterPillKey[]}
              selectedFilters={selectedFilters}
              timezone={timezone}
              isPublicaAnalytics={true}
              handleOpenFilter={handleOpenFilter}
              handleCloseFilter={handleCloseFilter}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding enableSize>
        <Rows
          style={{
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: '4px',
            backgroundColor: publicaColors.white,
          }}
        >
          <Row style={{ padding: '8px 16px 0px' }}>
            <Text small bold>
              Performances
            </Text>
          </Row>
          <Row style={{ padding: '8px 16px 16px' }}>
            <DashboardCardsV2
              availableMetrics={extractViableLiveMetrics(
                cfg.cards as unknown as IDashboardHomeCardsConfig,
                cfg.cards.groupings[cardGroup],
              )}
              displayedMetricKeys={displayedMetricKeys}
              cardMetrics={data.CardMetrics}
              loadState={loadState}
              activeChartMetrics={activeChartMetrics}
              handleOnClickCard={handleOnClickCard}
              handleOnChangeCardMetric={handleOnChangeCardMetric}
            />
          </Row>
          <Row>
            <CompareChart
              loadState={loadState}
              data={mergeCompareData(data.LiveData)}
              metrics={activeChartMetrics}
              metricsConfig={cfg.cards.metrics}
              grouping={0}
              syncID={cfg.chartSyncID}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding enableSize>
        <Rows gap={spacing.base}>
          {cfg.subCharts.map((chart) => {
            return (
              <Row
                noHorizontalPadding
                extraSmallVerticalPadding
                style={{
                  border: `1px solid ${publicaColors.lightGray}`,
                  borderRadius: '4px',
                  backgroundColor: publicaColors.white,
                }}
              >
                {chart.dropdownFilter ? (
                  <DashboardSubChartMetricsSelector
                    title={chart.title}
                    loadState={loadState}
                    data={extractDataForMultiLineChart(
                      data.LiveData || [],
                      Object.keys(chart.metrics),
                    )}
                    metricsCfg={chart.metrics}
                    orderedMetricKeys={
                      orderedBidErrorKeys.length > 0
                        ? orderedBidErrorKeys.map(([key, count]) => [
                            key as keyof IDashboardChartData,
                            count,
                          ])
                        : chart.orderedMetricKeys.map((key) => [key, 0])
                    }
                    activeFilters={activeFilters}
                    syncID={cfg.chartSyncID}
                    defaultActiveMetrics={orderedBidErrorKeys.slice(0, 5).map(([key]) => key)}
                  />
                ) : (
                  <DashboardSubChart
                    title={chart.title}
                    loadState={loadState}
                    data={extractDataForMultiLineChart(
                      data.LiveData || [],
                      Object.keys(chart.metrics),
                    )}
                    metricsCfg={chart.metrics}
                    orderedMetricKeys={chart.orderedMetricKeys}
                    activeFilters={activeFilters}
                    syncID={cfg.chartSyncID}
                  />
                )}
              </Row>
            );
          })}
        </Rows>
      </Panel>
      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
